// @flow
import productTypeData from '../../data/ProductType';
import productTypeNames from '../../../data/product-data';

// TODO MB-3674
import { getDefaultProductSettings } from '../../products/reducers/settings/SettingsHelpers';
import type { ProductSizes, ProductSizeType } from '../../../api/types/ProductSizes';

type intlMessage = {
  id: string,
  defaultMessage: string,
};

let productTypes = productTypeData;

let hasSetFullUploadElements = false;
let productSizeMetadata = {};

type Sizes = Array<{ id: string }>;
type ProdType = { sizes: Sizes };

const allSizes = Object.entries(productTypes).reduce((acc, [, productType]) => {
  const { sizes } = ((productType: any): ProdType);
  return [...acc, ...sizes];
}, []);

const getProductSizes = (productTypeId: ?string | null): Sizes => {
  return productTypeId === 'all' ? allSizes : productTypes[productTypeId]?.sizes || [];
};

const getProductSizeIntlSingularKey = (productSizeId: string): intlMessage =>
  productTypeNames[`productSize_${productSizeId}_singular`];
const getProductSizeIntlPluralKey = (productSizeId: string): intlMessage => {
  return (
    productTypeNames[`productSize_${productSizeId}_plural`] || productTypeNames[`productSize_${productSizeId}_singular`]
  );
};
const getProductTypeIntlPluralKey = (productTypeId: string): intlMessage =>
  productTypeNames[`productType_${productTypeId}_plural`];
const getProductTypeIntlSingularKey = (productTypeId: string): intlMessage =>
  productTypeNames[`productType_${productTypeId}_singular`];
const getProductTypeIds = (): Array<string> => Object.keys(productTypes);

function getProductSizeItemMetadata(sizeId: string) {
  return productSizeMetadata[sizeId];
}

function setProductTypes(data: Object) {
  productTypes = data;
}

const getFullUploadCreationLink = (productSizeId: string): string => {
  if (productSizeMetadata[productSizeId]) {
    return productSizeMetadata[productSizeId].pixelCreationUrl;
  }
  return '';
};

const getFullUploadProductTypeIds = (): Array<string> => {
  // TODO should this exists, getProductTypeIds can be used
  return getProductTypeIds();
};

const getFullUploadProductSizes = (productTypeId: string): Array<ProductSizeType> => {
  const productTypeSizes = getProductSizes(productTypeId);

  return productTypeSizes
    .map((sizeItem) => {
      // Only exclude if explicitly unavailable on the current store
      const productSizeData = getProductSizeItemMetadata(sizeItem.id) || { availableInStore: false };
      return productSizeData.availableInStore === false ? null : productSizeData;
    })
    .filter((sizeItem) => sizeItem !== null);
};

const getFullUploadElements = (productSizeId: string): Array<Object> => {
  const productSizeData = productSizeMetadata[productSizeId];
  if (productSizeData && productSizeData.elements) {
    return productSizeData.elements.map((element) => ({
      ...element,
      imageUrl: `/static-assets/mbs/frontend/resources/full-upload-finishes/v3/x1/${productSizeId}/${element.id.toLowerCase()}.png`,
    }));
  }
  return [];
};

/**
 * Returns true if there is at least one tailored element for this product size in Full Upload.
 */
const areFullUploadElementsAvailable = (productSizeId: string): boolean => {
  return getFullUploadElements(productSizeId).length > 0;
};

/* Returns if there any templates available for a specific productType */
const isProductTypeAvailable = (productTypeId: string, availableProductSizes: ProductSizes = {}): boolean => {
  const typeSizes = getProductSizes(productTypeId);

  return typeSizes.some((productSize) => {
    const productSizeInfo = availableProductSizes[productSize.id] || { templatesCount: 0 };
    return productSizeInfo.templatesCount > 0;
  });
};

/* Returns if there any templates available for any of the products */
const areTemplatesAvailable = (availableProductSizes: ProductSizes = {}): boolean => {
  // if availableProductSizes is an empty object then return false
  if (Object.keys(availableProductSizes).length === 0 && availableProductSizes.constructor === Object) {
    return false;
  }

  const productSizesKeys = Object.keys(availableProductSizes);

  const templatesCount = productSizesKeys.reduce((previousTotal, key) => {
    return previousTotal + availableProductSizes[key].templatesCount;
  }, 0);

  return templatesCount > 0;
};

const getSingleProductTypeAvailable = (availableProductSizes: ProductSizes = {}): string | null => {
  const totalProducts = getProductTypeIds().filter((productTypeId) =>
    isProductTypeAvailable(productTypeId, availableProductSizes),
  );

  return totalProducts.length === 1 ? totalProducts[0] : null;
};

const isSingleTemplateAvailable = (availableProductSizes: ProductSizes = {}): boolean => {
  let totalTemplates = 0;
  getProductTypeIds().forEach((productTypeId) => {
    const typeSizes = getProductSizes(productTypeId);
    totalTemplates += typeSizes.reduce((sizeTemplates, productSize) => {
      const productSizeInfo = availableProductSizes[productSize.id] || { templatesCount: 0 };
      return sizeTemplates + productSizeInfo.templatesCount;
    }, 0);
  });

  return totalTemplates === 1;
};

const getValidProductSize = (productSize: string): string => {
  if (allSizes.map((item) => item.id).indexOf(productSize) > -1) {
    return productSize;
  }

  return 'ALL';
};

const getValidSort = (sort: string): string => {
  if (['updatedDesc', 'nameAsc', 'updatedAsc'].indexOf(sort) > -1) {
    return sort;
  }
  return 'updatedDesc';
};

const isFilterApplied = (props: { productSize: string, template: string, searchTerm: string }): boolean => {
  const { productSize, template, searchTerm } = props;
  const settings = getDefaultProductSettings();
  return productSize !== settings.productSize || template !== settings.template || searchTerm !== settings.searchTerm;
};

const validateMetadata = (metadata: ProductSizeType): ProductSizeType => {
  const validatedMetadata = {};

  getProductTypeIds().forEach((productTypeId) => {
    getProductSizes(productTypeId).forEach((productSize) => {
      const productSizeId = productSize.id;
      if (metadata[productSizeId]) {
        const sizeData = metadata[productSizeId];
        validatedMetadata[productSizeId] = { ...sizeData, id: productSizeId };
      }
    });
  });

  return validatedMetadata;
};

const setProductSizeMetadata = (metadata: ProductSizeType): void => {
  productSizeMetadata = validateMetadata(metadata);
  hasSetFullUploadElements = true;
};

const hasLoadedFullUploadElements = () => {
  return hasSetFullUploadElements;
};

export {
  getProductTypeIds,
  getFullUploadProductTypeIds,
  getFullUploadProductSizes,
  getFullUploadCreationLink,
  areFullUploadElementsAvailable,
  getFullUploadElements,
  getProductSizes,
  setProductSizeMetadata,
  getProductSizeIntlSingularKey,
  getProductSizeIntlPluralKey,
  getProductTypeIntlPluralKey,
  getProductTypeIntlSingularKey,
  isProductTypeAvailable,
  getSingleProductTypeAvailable,
  isSingleTemplateAvailable,
  getValidProductSize,
  getValidSort,
  isFilterApplied,
  areTemplatesAvailable,
  validateMetadata,
  getProductSizeItemMetadata,
  setProductTypes,
  hasLoadedFullUploadElements,
};
